import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { safeNextPath } from '@affily/shared/redirect';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { useBrand } from '../hooks/useBrand.jsx';
import { rule } from '../lib/fieldRules.js';
import { Seo } from '../components/Seo.jsx';
import { SignInButtons } from '../components/SignInButtons.jsx';
import { Button, Field, Input, Alert } from '../components/ui.jsx';

const roleCopy = {
  creator: {
    title: 'Creator',
    blurb: 'Get UGC deals and brand partnerships. Publish your rates.',
  },
  brand: {
    title: 'Brand',
    blurb: 'List programs with public terms. Find creators with real stats.',
  },
};

// Where each role lands when nothing sent them somewhere more specific.
const DEFAULT_NEXT = {
  creator: '/dashboard/creator/profile',
  brand: '/dashboard/brand/profile',
};

export default function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signup } = useAuth();
  const brands = useBrand();

  const [role, setRole] = useState(params.get('role') === 'brand' ? 'brand' : 'creator');
  const [form, setForm] = useState({ name: '', email: '', password: '', companyName: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // `next` is read off a URL anyone can craft, so only a path on this site is used.
  const next = safeNextPath(params.get('next'));
  const destination = next ?? DEFAULT_NEXT[role];
  // The sign-in page needs it too, or a visitor who switches over loses where
  // they were going.
  const loginHref = next ? `/login?next=${encodeURIComponent(next)}` : '/login';

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const { companyName, ...account } = form;
      await signup({ ...account, role });

      // A brand that names its company here starts with a profile. The account
      // exists either way, so a failure must not strand them on this form: the
      // profile page will let them try again.
      const company = companyName.trim();
      if (role === 'brand' && company) {
        try {
          const created = await api.post('/brands', { companyName: company });
          await brands.reload(created.brand.id);
        } catch {
          // Deliberately swallowed, see above.
        }
      }
      navigate(destination);
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        title="Join free"
        description="Join Affily free as a creator or a brand: partnership programs, tracking and payouts in one place."
        noindex
      />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Link to="/" className="text-2xs font-semibold uppercase tracking-widest text-accent">
          Affily
        </Link>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Join free</h1>
        <p className="mt-1.5 text-sm text-ink-muted">
          Free for creators and brands. You can add the other role later from one account.
        </p>

        <fieldset className="mt-6">
          <legend className="sr-only">Account type</legend>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(roleCopy).map(([key, copy]) => (
              <button
                key={key}
                type="button"
                onClick={() => setRole(key)}
                aria-pressed={role === key}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  role === key
                    ? 'border-accent bg-accent-soft'
                    : 'border-rule-strong bg-paper-raised hover:bg-paper-sunken'
                }`}
              >
                <span className="block text-sm font-medium">{copy.title}</span>
                <span className="mt-0.5 block text-xs text-ink-muted">{copy.blurb}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <div className="mt-6">
          <SignInButtons role={role} next={destination} onSuccess={() => navigate(destination)} onError={setError} />
        </div>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <Alert>{error}</Alert>

          <Field label="Name" required error={fieldErrors.name} constraint={rule.max(120)}>
            <Input
              value={form.name}
              onChange={set('name')}
              autoComplete="name"
              required
              placeholder={role === 'brand' ? 'Your name' : 'Your display name'}
            />
          </Field>

          {role === 'brand' && (
            <Field
              label="Company name"
              error={fieldErrors.companyName}
              constraint={rule.max(120)}
              hint="Optional — you can set this up later."
            >
              <Input
                value={form.companyName}
                onChange={set('companyName')}
                autoComplete="organization"
                maxLength={120}
              />
            </Field>
          )}

          {/* "username", not "email": it is the field Safari (and other password
              managers) files the saved password under. */}
          <Field label="Email" required error={fieldErrors.email} constraint={rule.email()}>
            <Input
              type="email"
              name="email"
              value={form.email}
              onChange={set('email')}
              autoComplete="username"
              required
            />
          </Field>

          <Field
            label="Password"
            required
            constraint={rule.password()}
            error={fieldErrors.password}
          >
            <Input
              type="password"
              name="password"
              value={form.password}
              onChange={set('password')}
              autoComplete="new-password"
              minLength={10}
              required
            />
          </Field>

          <Button type="submit" size="lg" className="w-full" disabled={busy}>
            {busy ? 'Creating account…' : `Create ${roleCopy[role].title.toLowerCase()} account`}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-ink-muted">
          Already have an account?{' '}
          <Link to={loginHref} className="font-medium text-accent hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </>
  );
}
